<template>
    <UInputMenu
        ref="inputMenuRef"
        v-model="selection"
        v-model:search-term="searchTerm"
        :open="isOpen"
        :items="filteredOptions"
        :label-key="optionLabel ?? 'label'"
        :multiple="allowMultiple"
        :placeholder="placeholder"
        :create-item="!forceSelection"
        :loading="loading"
        :trailing="showDropdownButton"
        :open-on-click="showDropdownButton"
        :id="inputId"
        ignore-filter
        :ui="ui"
        @change="$emit('change', $event)"
        @update:open="onOpenUpdate"
        @create="onCreate"
    />
</template>

<script setup>
import { computed, ref, watch } from 'vue'

// Define props
const props = defineProps({
    allowMultiple: {
        default: false,
        required: false,
        type: Boolean,
    },
    filterLabel: {
        default: 'name',
        required: false,
        type: String,
    },
    optionLabel: {
        default: null,
        required: false,
        type: String,
    },
    /** @type {import('vue').PropType<string|null>} */
    optionValue: {
        default: null,
        required: false,
        type: String,
    },
    options: {
        default: () => [],
        required: false,
        type: Array,
    },
    // Async remote search, e.g. `(query) => userService.search({ search: query })`.
    // When set, options are fetched per keystroke instead of filtered client-side —
    // use this for pickers backed by large/paginated datasets (users, teams).
    /** @type {import('vue').PropType<((query: string) => Promise<Array>)|null>} */
    search: {
        default: null,
        required: false,
        type: Function,
    },
    placeholder: {
        default: '',
        required: false,
        type: String,
    },
    forceSelection: {
        default: true,
        required: false,
        type: Boolean,
    },
    showDropdownButton: {
        default: false,
        required: false,
        type: Boolean,
    },
    minLength: {
        default: 1,
        required: false,
        type: Number,
    },
    inputId: {
        default: null,
        required: false,
        type: String,
    },
})

// Define emits
defineEmits(['change'])

const model = defineModel()

// Reactive data
const searchTerm = ref('')
const filteredOptions = ref([])
const loading = ref(false)
// Remote-mode selections aren't present in a local `options` list, so the
// picked option is cached here to keep the input's display label after selection.
/** @type {import('vue').Ref<Object|null>} */
const lastRemoteSelection = ref(null)

// When `optionValue` is set, the public model holds the extracted value (e.g. a
// ulid) while the underlying picker's own v-model needs the full option object
// to render the label — this bridges the two. Without `optionValue` it's a
// plain passthrough.
const selection = computed({
    get() {
        if (typeof props.optionValue !== 'string') {
            return model.value
        }

        if (model.value == null || model.value === '') {
            return null
        }

        if (
            lastRemoteSelection.value instanceof Object &&
            lastRemoteSelection.value[props.optionValue] === model.value
        ) {
            return lastRemoteSelection.value
        }

        return (
            props.options.find(
                (option) => option?.[props.optionValue] === model.value
            ) ?? null
        )
    },
    set(value) {
        if (typeof props.optionValue !== 'string') {
            model.value = value
            return
        }

        if (value == null || typeof value !== 'object') {
            model.value = ''
            return
        }

        lastRemoteSelection.value = value
        model.value = value[props.optionValue] ?? ''
    },
})

// Guards against out-of-order responses: only the most recently issued
// search's result is applied, so a slow earlier keystroke can't clobber
// a faster later one.
let searchSeq = 0

async function runSearch(query) {
    if (query.length < props.minLength) {
        filteredOptions.value = []
        return
    }

    if (typeof props.search === 'function') {
        const seq = ++searchSeq
        loading.value = true
        try {
            const results = await props.search(query)
            if (seq === searchSeq) {
                filteredOptions.value = results
            }
        } finally {
            if (seq === searchSeq) {
                loading.value = false
            }
        }
        return
    }

    filteredOptions.value = props.options.filter((value) => {
        if (typeof value === 'string') {
            return value.toLowerCase().includes(query.toLowerCase())
        }

        return (
            value !== null &&
            typeof value === 'object' &&
            props.filterLabel in value &&
            typeof value[props.filterLabel] === 'string' &&
            value[props.filterLabel].toLowerCase().includes(query.toLowerCase())
        )
    })
}

// Local filtering is cheap and synchronous, so it runs on every keystroke.
// A remote `search` hits a real endpoint, so it's debounced the way
// PrimeVue's AutoComplete did by default — otherwise fast typing fires one
// request per keystroke.
const SEARCH_DEBOUNCE_MS = 300
let debounceTimer = null

function scheduleSearch(query) {
    clearTimeout(debounceTimer)

    if (typeof props.search !== 'function') {
        runSearch(query)
        return
    }

    debounceTimer = setTimeout(() => runSearch(query), SEARCH_DEBOUNCE_MS)
}

watch(searchTerm, (query) => scheduleSearch(query ?? ''))

const inputMenuRef = ref(null)

// Controlled so `onCreate` below can force the panel closed after committing
// a free-text value — NuxtUI's create-item entry is deliberately inert (see
// onCreate) and never closes the panel on its own, and blurring the input
// alone doesn't reliably do it either (the panel's own focus handling can
// reclaim focus immediately).
const isOpen = ref(false)

// Loads suggestions as soon as the panel opens (e.g. clicking the dropdown
// button with `minLength: 0`), rather than waiting for the first keystroke.
function onOpenUpdate(open) {
    isOpen.value = open
    if (open) {
        clearTimeout(debounceTimer)
        runSearch(searchTerm.value ?? '')
    }
}

// NuxtUI's create-item entry only emits `create` — it doesn't commit the
// typed text as the value itself, since it's meant as a hook for "create a
// new record" flows. Here a "new" value is just the typed text, mirroring
// PrimeVue's forceSelection:false behavior of accepting free text.
function onCreate(text) {
    selection.value = text
    isOpen.value = false
    inputMenuRef.value?.inputRef?.blur()
}

const ui = {
    root: 'flex items-center flex-wrap w-full border border-taupe-300 rounded-md bg-white focus-within:border-sky-500 focus-within:ring-1 focus-within:ring-sky-500',
    base: 'text-sm w-full min-w-0 px-3 py-2 text-taupe-800 border-0 bg-transparent focus:outline-none focus:ring-0',
    trailing:
        'shrink-0 self-stretch flex items-center justify-center px-2 text-taupe-500',
    trailingIcon: 'text-taupe-500',
    tagsInput:
        'text-sm w-full min-w-0 px-1 py-1 text-taupe-800 border-0 bg-transparent focus:outline-none focus:ring-0',
    content:
        'border border-taupe-300 rounded-md mt-0.5 bg-white ring-0 shadow-none',
    viewport: 'overflow-y-auto',
    item: 'cursor-pointer px-3 py-2 text-sm rounded-sm hover:bg-sky-200/50',
    empty: 'px-2 py-3 text-sm italic',
    tagsItem:
        'bg-sky-200/50 px-3 py-1 flex items-center gap-2 rounded-full ring-0',
    tagsItemText: 'inline-block w-fit text-sm',
    tagsItemDelete: 'mt-0 cursor-pointer',
}
</script>
